/**
 * NumerologyPage — страница нумерологии с 6 табами
 *
 * Табы: Профиль | Пифагор | Матрица | Циклы | Даты | Имя
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import AccessBadge from '../components/access/AccessBadge';
import { useSubscriptionAccess } from '../hooks/useSubscriptionAccess';
import { SubscriptionTier } from '../../shared/types/access';
import api from '../api';
import AnimatedLoadingState from '../components/AnimatedLoadingState';
import ErrorState from '../components/ErrorState';
import NumerologyLab from '../components/NumerologyLab';
import PythagoreanSquare from '../components/PythagoreanSquare';
import DestinyMatrix from '../components/DestinyMatrix';
import KarmaNumber from '../components/KarmaNumber';
import MaturityNumber from '../components/MaturityNumber';
import BalanceNumber from '../components/BalanceNumber';
import SurvivalNumber from '../components/SurvivalNumber';
import { setMetaTags, resetMetaTags, buildCanonicalUrl, PAGE_SEO } from '../utils/seo';

// ============================================================
// Типы
// ============================================================

interface NumerologyProfile {
  lifePathNumber: number;
  destinyNumber: number;
  soulUrgeNumber: number;
  personalityNumber: number;
}

interface ProfileResponse {
  success: boolean;
  data?: NumerologyProfile;
  error?: string;
}

interface PythagoreanSquareCell {
  position: number;
  name: string;
  values: number[];
  count: number;
  interpretation: string;
}

interface PythagoreanSquareResponse {
  success: boolean;
  data?: {
    cells: PythagoreanSquareCell[];
    grid: number[][];
    energyBalance: { strong: string[]; weak: string[]; balanced: string[] };
    aiInterpretation?: string;
  };
  error?: string;
}

interface Arcanum {
  number: number;
  name: string;
  keywords: string;
  positive: string;
  negative: string;
}

interface DestinyMatrixResponse {
  success: boolean;
  data?: {
    center: Arcanum;
    corners: Arcanum[];
    edges: Arcanum[];
    keyPoints: Arcanum[];
    svg: string;
    aiInterpretation?: string;
  };
  error?: string;
}

interface PersonalCycleData {
  year: number;
  month: number;
  day: number;
  description: string;
}

interface CycleResponse {
  success: boolean;
  data?: PersonalCycleData;
  error?: string;
}

interface LuckyDate {
  date: string;
  type: string;
  description: string;
  score: number;
}

interface LuckyDatesResponse {
  success: boolean;
  data?: {
    luckyDates: LuckyDate[];
    aiForecast?: string;
  };
  error?: string;
}

interface NameAnalysisData {
  expression?: number;
  harmony?: number;
  balance?: number;
  maturity?: number;
  power?: number;
  soul?: number;
  letters?: Record<string, number>;
  aiInterpretation?: string;
  text?: string;
}
interface NameAnalysisResponse {
  success: boolean;
  data?: NameAnalysisData;
  error?: string;
}

interface KarmaInterpretation {
  number: number;
  title: string;
  description: string;
  lessons: string[];
  challenges: string[];
  strengths: string[];
  recommendations: string[];
}

interface KarmaNumberResponse {
  success: boolean;
  data?: {
    karmaNumbers: number[];
    hasKarmicDebt: boolean;
    interpretations: KarmaInterpretation[];
    aiInterpretation?: string;
  };
  error?: string;
}

interface MaturityNumberResponse {
  success: boolean;
  data?: {
    number: number;
    lifePathNumber: number;
    destinyNumber: number;
    description: string;
    interpretation: string;
    aiInterpretation?: string;
    recommendations: string[];
  };
  error?: string;
}

interface BalanceNumberResponse {
  success: boolean;
  data?: {
    number: number;
    description: string;
    interpretation: string;
    aiInterpretation?: string;
    copingStrategies: string[];
    challenges: string[];
    recommendations: string[];
  };
  error?: string;
}

interface SurvivalNumberResponse {
  success: boolean;
  data?: {
    number: number;
    description: string;
    interpretation: string;
    aiInterpretation?: string;
    instincts: string[];
    crisisResponse: string[];
    strengths: string[];
    recommendations: string[];
  };
  error?: string;
}

// ============================================================
// Стили
// ============================================================

const titleStyle: React.CSSProperties = {
  fontSize: 'clamp(28px, 5vw, 40px)',
  fontFamily: 'var(--font-display)',
  fontStyle: 'italic',
  background: 'linear-gradient(135deg, #F4E4A6 0%, #D4AF37 40%, #B8942B 70%, #F4E4A6 100%)',
  backgroundSize: '200% auto',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  marginBottom: '24px',
  textAlign: 'center',
  animation: 'goldShimmer 3s ease-in-out infinite',
};

const cardStyle: React.CSSProperties = {
  background: 'rgba(13, 20, 40, 0.85)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(212, 175, 55, 0.12)',
  borderRadius: '24px',
  boxShadow: '0 8px 40px rgba(212, 175, 55, 0.08), inset 0 0 40px rgba(13, 20, 40, 0.5)',
  padding: '32px 20px',
  marginBottom: '20px',
};

const buttonPrimaryStyle: React.CSSProperties = {
  background: 'linear-gradient(135deg, #d4af37 0%, #f4d03f 100%)',
  border: 'none',
  padding: '10px 24px',
  borderRadius: '25px',
  color: '#0a0814',
  fontSize: '14px',
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'var(--font-sans)',
  transition: 'all 0.3s',
  boxShadow: '0 4px 15px rgba(212, 175, 55, 0.3)',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 14px',
  background: 'rgba(10, 8, 20, 0.6)',
  border: '1px solid rgba(212, 175, 55, 0.25)',
  borderRadius: '10px',
  color: '#e0e0e0',
  fontSize: '14px',
  fontFamily: 'var(--font-sans)',
  outline: 'none',
  boxSizing: 'border-box',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  color: '#d4af37',
  fontSize: '13px',
  marginBottom: '6px',
  fontFamily: 'var(--font-sans)',
};

type TabKey = 'profile' | 'pythagorean' | 'matrix' | 'cycles' | 'dates' | 'name' | 'karma' | 'maturity' | 'balance' | 'survival';

const TABS: Array<{ key: TabKey; label: string; icon: string }> = [
  { key: 'profile', label: 'Профиль', icon: '' },
  { key: 'pythagorean', label: 'Пифагор', icon: '' },
  { key: 'matrix', label: 'Матрица', icon: '' },
  { key: 'cycles', label: 'Циклы', icon: '' },
  { key: 'dates', label: 'Даты', icon: '' },
  { key: 'name', label: 'Имя', icon: '' },
  { key: 'karma', label: 'Число кармы', icon: '🔮' },
  { key: 'maturity', label: 'Число зрелости', icon: '⭐' },
  { key: 'balance', label: 'Число баланса', icon: '⚖️' },
  { key: 'survival', label: 'Число выживания', icon: '🛡️' },
];

// ============================================================
// Компонент
// ============================================================

const NumerologyPage: React.FC = () => {
  const { user } = useAuth();
  const { hasAccess, userTier, requiredTier, isLoading: accessLoading } = useSubscriptionAccess('numerology-basic');
  const [activeTab, setActiveTab] = useState<TabKey>('profile');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // SEO
  useEffect(() => {
    setMetaTags({
      ...PAGE_SEO.numerology,
      canonicalUrl: buildCanonicalUrl('/numerology'),
    });
    return () => { resetMetaTags(); };
  }, []);

  // Профиль
  const [profile, setProfile] = useState<NumerologyProfile | null>(null);

  // Пифагор
  const [pythagoreanData, setPythagoreanData] = useState<PythagoreanSquareResponse['data'] | null>(null);

  // Матрица
  const [matrixData, setMatrixData] = useState<DestinyMatrixResponse['data'] | null>(null);

  // Циклы
  const [cycleData, setCycleData] = useState<PersonalCycleData | null>(null);

  // Даты
  const [luckyDates, setLuckyDates] = useState<LuckyDate[]>([]);
  const [luckyForecast, setLuckyForecast] = useState<string>('');
  const [datesMonth, setDatesMonth] = useState(new Date().getMonth() + 1);
  const [datesYear, setDatesYear] = useState(new Date().getFullYear());

  // Имя
  const [nameAnalysis, setNameAnalysis] = useState<string | null>(null);
  const [nameInput, setNameInput] = useState('');

  // Карма
  const [karmaData, setKarmaData] = useState<KarmaNumberResponse['data'] | null>(null);

  // Зрелость
  const [maturityData, setMaturityData] = useState<MaturityNumberResponse['data'] | null>(null);

  // Баланс
  const [balanceData, setBalanceData] = useState<BalanceNumberResponse['data'] | null>(null);

  // Выживание
  const [survivalData, setSurvivalData] = useState<SurvivalNumberResponse['data'] | null>(null);

  // Форма для профиля (скрыта если данные уже есть)
  const [profileBirthDate, setProfileBirthDate] = useState(user?.birth_date || '');
  const [profileName, setProfileName] = useState(user?.full_name || '');
  const [profileCalculating, setProfileCalculating] = useState(false);

  // ---------- Auto-calculate profile on mount ----------
  useEffect(() => {
    if (user?.birth_date && user?.full_name && !profile) {
      handleProfileAuto();
    }
  }, [user?.birth_date, user?.full_name]);

  // ---------- Handlers ----------

  // Профиль — автоматический расчёт из регистрационных данных
  const handleProfileAuto = async () => {
    if (!user?.birth_date || !user?.full_name) return;
    setError(null);
    setProfileCalculating(true);
    try {
      const res = await api.post('/numerology/profile', {
        birth_date: user.birth_date,
        full_name: user.full_name,
      });
      if (res.success && res.data) {
        const d = res.data as any;
        console.log('[NumerologyPage] Server response:', JSON.stringify(d, null, 2));

        // NaN guard: проверяем что числа валидны
        const num = (v: unknown): number | undefined =>
          typeof v === 'number' && !Number.isNaN(v) ? v : undefined;

        setProfile({
          lifePathNumber: num(d.lifePath?.number) ?? num(d.lifePathNumber) ?? 0,
          destinyNumber: num(d.destiny?.number) ?? num(d.destinyNumber) ?? 0,
          soulUrgeNumber: num(d.soul?.number) ?? num(d.soulUrgeNumber) ?? 0,
          personalityNumber: num(d.personality?.number) ?? num(d.personalityNumber) ?? 0,
        });
      } else {
        setError(res.error || 'Ошибка расчёта профиля');
      }
    } catch {
      setError('Ошибка при расчёте нумерологического профиля');
    } finally {
      setProfileCalculating(false);
    }
  };

  // Профиль — ручной расчёт (если данных нет)
  const handleProfile = async () => {
    if (loadingRef.current) return;
    setError(null);
    syncLoading(true);
    try {
      const res = await api.post('/numerology/profile', {
        birth_date: profileBirthDate,
        full_name: profileName,
      });
      if (res.success && res.data) {
        const d = res.data as any;
        console.log('[NumerologyPage] Server response:', JSON.stringify(d, null, 2));

        // NaN guard: проверяем что числа валидны
        const num = (v: unknown): number | undefined =>
          typeof v === 'number' && !Number.isNaN(v) ? v : undefined;

        setProfile({
          lifePathNumber: num(d.lifePath?.number) ?? num(d.lifePathNumber) ?? 0,
          destinyNumber: num(d.destiny?.number) ?? num(d.destinyNumber) ?? 0,
          soulUrgeNumber: num(d.soul?.number) ?? num(d.soulUrgeNumber) ?? 0,
          personalityNumber: num(d.personality?.number) ?? num(d.personalityNumber) ?? 0,
        });
      } else {
        setError(res.error || 'Ошибка расчёта профиля');
      }
    } catch {
      setError('Ошибка при расчёте нумерологического профиля');
    } finally {
      syncLoading(false);
    }
  };

  // Пифагор
  const loadPythagorean = useCallback(async () => {
    if (!user?.id || loadingRef.current) return;
    setError(null);
    syncLoading(true);
    try {
      const res = await api.get<PythagoreanSquareResponse['data']>(
        '/numerology/advanced/pythagorean-square'
      );
      if (res.success && res.data) {
        setPythagoreanData(res.data);
      } else {
        setError(res.error || 'Нет данных');
      }
    } catch {
      setError('Ошибка загрузки Пифагорейского квадрата');
    } finally {
      syncLoading(false);
    }
  }, [user?.id]);

  // Матрица
  const loadMatrix = useCallback(async () => {
    if (!user?.id || loadingRef.current) return;
    setError(null);
    syncLoading(true);
    try {
      const res = await api.get<DestinyMatrixResponse['data']>(
        '/numerology/advanced/destiny-matrix'
      );
      if (res.success && res.data) {
        setMatrixData(res.data);
      } else {
        setError(res.error || 'Нет данных');
      }
    } catch {
      setError('Ошибка загрузки Матрицы судьбы');
    } finally {
      syncLoading(false);
    }
  }, [user?.id]);

  // Циклы
  const loadCycles = async () => {
    if (!user?.id || loadingRef.current) return;
    setError(null);
    syncLoading(true);
    try {
      const res = await api.post<CycleResponse['data']>('/numerology/personal-cycle', {
        userId: user.id,
      });
      if (res.success && res.data) {
        setCycleData(res.data);
      } else {
        setError(res.error || 'Нет данных');
      }
    } catch {
      setError('Ошибка загрузки персональных циклов');
    } finally {
      syncLoading(false);
    }
  };

  // Даты
  const loadLuckyDates = useCallback(async () => {
    if (!user?.id || loadingRef.current) return;
    setError(null);
    syncLoading(true);
    try {
      const res = await api.get<LuckyDatesResponse['data']>(
        `/numerology/advanced/lucky-dates/${datesYear}/${datesMonth}`
      );
      if (res.success && res.data) {
        setLuckyDates(res.data.luckyDates || []);
        setLuckyForecast(res.data.aiForecast || '');
      } else {
        setLuckyDates([]);
        setLuckyForecast('');
      }
    } catch {
      setError('Ошибка загрузки благоприятных дат');
    } finally {
      syncLoading(false);
    }
  }, [user?.id, datesYear, datesMonth]);

  // Имя
  const loadNameAnalysis = async () => {
    if (!nameInput.trim() || loadingRef.current) return;
    setError(null);
    syncLoading(true);
    try {
      const res = await api.get<NameAnalysisResponse['data']>(
        `/numerology/advanced/name-analysis?name=${encodeURIComponent(nameInput.trim())}`
      );
      if (res.success && res.data) {
        const d = res.data;
        if (d.text) {
          setNameAnalysis(d.text);
        } else {
          const lines = [
            `Число Экспрессии: ${d.expression ?? '—'}`,
            `Число Гармонии (Души): ${d.harmony ?? '—'}`,
            `Число Равновесия (Личности): ${d.balance ?? '—'}`,
            `Число Зрелости: ${d.maturity ?? '—'}`,
            `Число Силы: ${d.power ?? '—'}`,
          ];
          if (d.aiInterpretation) lines.push('', d.aiInterpretation);
          setNameAnalysis(lines.join('\n'));
        }
      } else {
        setNameAnalysis(null);
      }
    } catch {
      setError('Ошибка анализа имени');
    } finally {
      syncLoading(false);
    }
  };

  // Карма
  const loadKarma = useCallback(async () => {
    if (!user?.id || loadingRef.current) return;
    setError(null);
    syncLoading(true);
    try {
      const res = await api.get<KarmaNumberResponse['data']>(
        '/numerology/karma'
      );
      if (res.success && res.data) {
        setKarmaData(res.data);
      } else {
        setError(res.error || 'Нет данных');
      }
    } catch {
      setError('Ошибка загрузки числа кармы');
    } finally {
      syncLoading(false);
    }
  }, [user?.id]);

  // Зрелость
  const loadMaturity = useCallback(async () => {
    if (!user?.id || loadingRef.current) return;
    setError(null);
    syncLoading(true);
    try {
      const res = await api.get<MaturityNumberResponse['data']>(
        '/numerology/maturity'
      );
      if (res.success && res.data) {
        setMaturityData(res.data);
      } else {
        setError(res.error || 'Нет данных');
      }
    } catch {
      setError('Ошибка загрузки числа зрелости');
    } finally {
      syncLoading(false);
    }
  }, [user?.id]);

  // Баланс
  const loadBalance = useCallback(async () => {
    if (!user?.id || loadingRef.current) return;
    setError(null);
    syncLoading(true);
    try {
      const res = await api.get<BalanceNumberResponse['data']>(
        '/numerology/balance'
      );
      if (res.success && res.data) {
        setBalanceData(res.data);
      } else {
        setError(res.error || 'Нет данных');
      }
    } catch {
      setError('Ошибка загрузки числа баланса');
    } finally {
      syncLoading(false);
    }
  }, [user?.id]);

  // Выживание
  const loadSurvival = useCallback(async () => {
    if (!user?.id || loadingRef.current) return;
    setError(null);
    syncLoading(true);
    try {
      const res = await api.get<SurvivalNumberResponse['data']>(
        '/numerology/survival'
      );
      if (res.success && res.data) {
        setSurvivalData(res.data);
      } else {
        setError(res.error || 'Нет данных');
      }
    } catch {
      setError('Ошибка загрузки числа выживания');
    } finally {
      syncLoading(false);
    }
  }, [user?.id]);

  // Ref для loading — чтобы guard работал актуально (closure не устаревает)
  const loadingRef = React.useRef(false);
  const syncLoading = (val: boolean) => { loadingRef.current = val; setLoading(val); };

  // Auto-load on tab switch
  React.useEffect(() => {
    if (loadingRef.current) return;
    switch (activeTab) {
      case 'pythagorean':
        if (!pythagoreanData) loadPythagorean();
        break;
      case 'matrix':
        if (!matrixData) loadMatrix();
        break;
      case 'cycles':
        if (!cycleData) loadCycles();
        break;
      case 'dates':
        if (luckyDates.length === 0) loadLuckyDates();
        break;
      case 'karma':
        if (!karmaData) loadKarma();
        break;
      case 'maturity':
        if (!maturityData) loadMaturity();
        break;
      case 'balance':
        if (!balanceData) loadBalance();
        break;
      case 'survival':
        if (!survivalData) loadSurvival();
        break;
    }
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------- Render ----------

  // Всегда рендерим страницу, даже если данные загружаются
  const tabButtonStyle = (active: boolean): React.CSSProperties => ({
    padding: isMobile ? '8px 12px' : '10px 20px',
    border: 'none',
    borderRadius: active ? '12px 12px 0 0' : '12px',
    fontSize: isMobile ? '12px' : '14px',
    fontFamily: 'var(--font-sans)',
    cursor: 'pointer',
    transition: 'all 0.3s',
    background: active
      ? 'rgba(212, 175, 55, 0.15)'
      : 'rgba(13, 20, 40, 0.6)',
    color: active ? '#d4af37' : '#888',
    borderBottom: active ? '2px solid #d4af37' : '1px solid transparent',
    fontWeight: active ? 600 : 400,
  });

  return (
    <div style={{
      minHeight: '80vh',
      padding: isMobile ? '16px 12px' : '20px 20px',
      maxWidth: '900px',
      margin: '0 auto',
      position: 'relative',
    }}>
      <div style={{ position: 'absolute', top: 0, left: '10%', right: '10%', height: '2px', background: 'linear-gradient(90deg, transparent, #d4af37, transparent)' }} />
      <h1 style={{...titleStyle, fontSize: isMobile ? 'clamp(22px, 5vw, 32px)' : titleStyle.fontSize}}>Нумерология</h1>

      {/* AccessBadge — показывает статус доступа */}
      {!accessLoading && (
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <AccessBadge
            featureId="numerology-basic"
            userTier={userTier as SubscriptionTier}
            requiredTier={requiredTier as SubscriptionTier}
            featureName="Нумерология"
            size="sm"
          />
        </div>
      )}

      {error && <ErrorState message={error} onRetry={() => setError(null)} />}

      {/* Табы */}
      <div style={{
        display: 'flex',
        gap: '4px',
        marginBottom: '20px',
        flexWrap: 'wrap',
        justifyContent: 'center',
      }}>
        {TABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={tabButtonStyle(activeTab === tab.key)}
          >
            {tab.icon && <span style={{ marginRight: '6px' }}>{tab.icon}</span>}
            {tab.label}
          </button>
        ))}
      </div>

      {/* ---------- Профиль ---------- */}
      {activeTab === 'profile' && (
        <div style={cardStyle}>
          <h2 style={{
            color: '#d4af37',
            fontSize: isMobile ? '16px' : '18px',
            fontFamily: 'var(--font-display)',
            fontStyle: 'italic',
            marginBottom: '16px',
            textAlign: 'center',
          }}>
            Нумерологический профиль
          </h2>

          {/* Автоматическая загрузка при наличии данных */}
          {profileCalculating && (
            <AnimatedLoadingState />
          )}

          {/* Ошибка расчёта — показать форму */}
          {!profile && !profileCalculating && error && (
            <div style={{ display: 'grid', gap: '16px', maxWidth: '400px', margin: '0 auto 20px' }}>
              <div style={{ color: '#FF6666', fontSize: '14px', textAlign: 'center' }}>{error}</div>
              <div>
                <label style={labelStyle}>Дата рождения</label>
                <input
                  type="date"
                  value={profileBirthDate}
                  onChange={e => setProfileBirthDate(e.target.value)}
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>Полное имя</label>
                <input
                  type="text"
                  placeholder="Иванов Иван Иванович"
                  value={profileName}
                  onChange={e => setProfileName(e.target.value)}
                  style={inputStyle}
                />
              </div>
              <button
                onClick={handleProfile}
                disabled={!profileBirthDate || !profileName}
                style={{
                  ...buttonPrimaryStyle,
                  opacity: !profileBirthDate || !profileName ? 0.5 : 1,
                  cursor: !profileBirthDate || !profileName ? 'not-allowed' : 'pointer',
                  alignSelf: 'center',
                }}
              >
                Рассчитать
              </button>
            </div>
          )}

          {/* Нет данных у пользователя — показать форму */}
          {!profile && !profileCalculating && !user?.birth_date && !user?.full_name && (
            <div style={{ display: 'grid', gap: '16px', maxWidth: '400px', margin: '0 auto 20px' }}>
              <div>
                <label style={labelStyle}>Дата рождения</label>
                <input
                  type="date"
                  value={profileBirthDate}
                  onChange={e => setProfileBirthDate(e.target.value)}
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>Полное имя</label>
                <input
                  type="text"
                  placeholder="Иванов Иван Иванович"
                  value={profileName}
                  onChange={e => setProfileName(e.target.value)}
                  style={inputStyle}
                />
              </div>
              <button
                onClick={handleProfile}
                disabled={!profileBirthDate || !profileName}
                style={{
                  ...buttonPrimaryStyle,
                  opacity: !profileBirthDate || !profileName ? 0.5 : 1,
                  cursor: !profileBirthDate || !profileName ? 'not-allowed' : 'pointer',
                  alignSelf: 'center',
                }}
              >
                Рассчитать
              </button>
            </div>
          )}

          {/* Данные есть у пользователя, но профиль ещё не рассчитан — кнопка */}
          {!profile && !profileCalculating && user?.birth_date && user?.full_name && (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <p style={{ color: '#888', marginBottom: '16px' }}>Нажмите для расчёта</p>
              <button onClick={handleProfileAuto} style={buttonPrimaryStyle}>
                Рассчитать профиль
              </button>
            </div>
          )}

          {profile && (
            <NumerologyLab
              lifePathNumber={profile.lifePathNumber}
              destinyNumber={profile.destinyNumber}
              soulUrgeNumber={profile.soulUrgeNumber}
              personalityNumber={profile.personalityNumber}
            />
          )}
        </div>
      )}

      {/* ---------- Пифагор ---------- */}
      {activeTab === 'pythagorean' && (
        <div style={cardStyle}>
          {loading ? (
            <AnimatedLoadingState />
          ) : pythagoreanData ? (
            <PythagoreanSquare data={pythagoreanData} />
          ) : (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <p style={{ color: '#888', marginBottom: '16px' }}>Нет данных для отображения</p>
              <button onClick={loadPythagorean} style={buttonPrimaryStyle}>
                Загрузить
              </button>
            </div>
          )}
        </div>
      )}

      {/* ---------- Матрица ---------- */}
      {activeTab === 'matrix' && (
        <div style={cardStyle}>
          {loading ? (
            <AnimatedLoadingState />
          ) : matrixData ? (
            <DestinyMatrix data={matrixData} />
          ) : (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <p style={{ color: '#888', marginBottom: '16px' }}>Нет данных для отображения</p>
              <button onClick={loadMatrix} style={buttonPrimaryStyle}>
                Загрузить
              </button>
            </div>
          )}
        </div>
      )}

      {/* ---------- Циклы ---------- */}
      {activeTab === 'cycles' && (
        <div style={cardStyle}>
          {loading ? (
            <AnimatedLoadingState />
          ) : cycleData ? (
            <div>
              <h3 style={{
                color: '#d4af37',
                fontSize: isMobile ? '16px' : '18px',
                fontFamily: 'var(--font-display)',
                fontStyle: 'italic',
                marginBottom: '16px',
                textAlign: 'center',
              }}>
                Персональные циклы
              </h3>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                gap: '16px',
                marginBottom: '20px',
              }}>
                <div style={{
                  ...numberCardStyle,
                  borderColor: 'rgba(212, 175, 55, 0.3)',
                }}>
                  <div style={{ fontSize: '12px', color: '#888', marginBottom: '8px' }}>Год</div>
                  <div style={{ fontSize: '36px', fontWeight: 700, color: '#d4af37' }}>
                    {typeof cycleData.year === 'object' ? cycleData.year.number : cycleData.year}
                  </div>
                </div>
                <div style={{
                  ...numberCardStyle,
                  borderColor: 'rgba(212, 175, 55, 0.25)',
                }}>
                  <div style={{ fontSize: '12px', color: '#888', marginBottom: '8px' }}>Месяц</div>
                  <div style={{ fontSize: '36px', fontWeight: 700, color: '#d4af37' }}>
                    {typeof cycleData.month === 'object' ? cycleData.month.number : cycleData.month}
                  </div>
                </div>
                <div style={{
                  ...numberCardStyle,
                  borderColor: 'rgba(147, 130, 220, 0.3)',
                }}>
                  <div style={{ fontSize: '12px', color: '#888', marginBottom: '8px' }}>День</div>
                  <div style={{ fontSize: '36px', fontWeight: 700, color: '#9382DC' }}>
                    {typeof cycleData.day === 'object' ? cycleData.day.number : cycleData.day}
                  </div>
                </div>
              </div>
              {(cycleData.description || cycleData.year?.interpretation) && (
                <div style={{
                  color: '#e0e0e0',
                  fontSize: '14px',
                  lineHeight: 1.7,
                  fontFamily: 'var(--font-sans)',
                  whiteSpace: 'pre-wrap',
                }}>
                  {cycleData.description || [cycleData.year?.interpretation, cycleData.month?.interpretation, cycleData.day?.interpretation].filter(Boolean).join('\n\n')}
                </div>
              )}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <p style={{ color: '#888', marginBottom: '16px' }}>Нет данных для отображения</p>
              <button onClick={loadCycles} style={buttonPrimaryStyle}>
                Рассчитать
              </button>
            </div>
          )}
        </div>
      )}

      {/* ---------- Даты ---------- */}
      {activeTab === 'dates' && (
        <div style={cardStyle}>
          <h3 style={{
            color: '#d4af37',
            fontSize: '18px',
            fontFamily: 'var(--font-display)',
            fontStyle: 'italic',
            marginBottom: '16px',
            textAlign: 'center',
          }}>
            Благоприятные даты
          </h3>

          {/* Выбор месяца */}
          <div style={{
            display: 'flex',
            gap: '12px',
            justifyContent: 'center',
            marginBottom: '20px',
          }}>
            <select
              value={datesMonth}
              onChange={e => setDatesMonth(Number(e.target.value))}
              style={{
                ...inputStyle,
                width: 'auto',
                padding: '8px 12px',
                appearance: 'auto',
              }}
            >
              {Array.from({ length: 12 }, (_, i) => (
                <option key={i + 1} value={i + 1}>
                  {MONTH_NAMES_RU[i]}
                </option>
              ))}
            </select>
            <select
              value={datesYear}
              onChange={e => setDatesYear(Number(e.target.value))}
              style={{
                ...inputStyle,
                width: 'auto',
                padding: '8px 12px',
                appearance: 'auto',
              }}
            >
              {[datesYear - 1, datesYear, datesYear + 1].map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
            <button onClick={loadLuckyDates} style={buttonPrimaryStyle}>
              Показать
            </button>
          </div>

          {loading ? (
            <AnimatedLoadingState />
          ) : luckyDates.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {luckyDates.map((d, i) => (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px 16px',
                    background: 'rgba(212, 175, 55, 0.06)',
                    borderRadius: '10px',
                    border: '1px solid rgba(212, 175, 55, 0.12)',
                  }}
                >
                  <div style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',
                    background: getScoreColor(d.score),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '12px',
                    fontWeight: 700,
                    color: '#0a0814',
                    flexShrink: 0,
                  }}>
                    {d.score}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '14px', color: '#e0e0e0', fontWeight: 500 }}>
                      {formatDateRu(d.date)}
                    </div>
                    <div style={{ fontSize: '12px', color: '#888' }}>
                      {d.type}
                    </div>
                  </div>
                  <div style={{
                    fontSize: '12px',
                    color: '#b0b0b0',
                    maxWidth: '200px',
                    textAlign: 'right',
                  }}>
                    {d.description}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p style={{ color: '#888', textAlign: 'center', padding: '20px 0' }}>
              Нет благоприятных дат для выбранного периода
            </p>
          )}

          {/* AI-прогноз благоприятных дат */}
          {luckyForecast && !loading && (
            <div style={{
              marginTop: '20px',
              padding: '20px',
              background: 'rgba(212, 175, 55, 0.04)',
              borderRadius: '12px',
              border: '1px solid rgba(212, 175, 55, 0.1)',
            }}>
              <h4 style={{
                color: '#d4af37',
                fontSize: '14px',
                fontFamily: 'var(--font-display)',
                fontStyle: 'italic',
                marginBottom: '12px',
              }}>
                Прогноз нумеролога
              </h4>
              <div style={{
                color: '#b0b0b0',
                fontSize: '14px',
                lineHeight: 1.7,
                whiteSpace: 'pre-wrap',
              }}>
                {luckyForecast}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ---------- Имя ---------- */}
      {activeTab === 'name' && (
        <div style={cardStyle}>
          <h3 style={{
            color: '#d4af37',
            fontSize: '18px',
            fontFamily: 'var(--font-display)',
            fontStyle: 'italic',
            marginBottom: '16px',
            textAlign: 'center',
          }}>
            Анализ имени
          </h3>

          <div style={{
            display: 'flex',
            gap: '12px',
            maxWidth: '500px',
            margin: '0 auto 20px',
          }}>
            <input
              type="text"
              placeholder="Введите имя"
              value={nameInput}
              onChange={e => setNameInput(e.target.value)}
              style={{ ...inputStyle, flex: 1 }}
              onKeyDown={e => { if (e.key === 'Enter') loadNameAnalysis(); }}
            />
            <button
              onClick={loadNameAnalysis}
              disabled={!nameInput.trim()}
              style={{
                ...buttonPrimaryStyle,
                opacity: !nameInput.trim() ? 0.5 : 1,
                cursor: !nameInput.trim() ? 'not-allowed' : 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              Анализировать
            </button>
          </div>

{loading && <AnimatedLoadingState />}

          {nameAnalysis && (
            <div style={{
              color: '#e0e0e0',
              fontSize: '14px',
              lineHeight: 1.7,
              fontFamily: 'var(--font-sans)',
              whiteSpace: 'pre-wrap',
              padding: '16px',
              background: 'rgba(212, 175, 55, 0.04)',
              borderRadius: '12px',
              border: '1px solid rgba(212, 175, 55, 0.12)',
            }}>
              {nameAnalysis}
            </div>
          )}
        </div>
      )}

      {/* ---------- Карма ---------- */}
      {activeTab === 'karma' && (
        <div style={cardStyle}>
          <h3 style={{
            color: '#d4af37',
            fontSize: '18px',
            fontFamily: 'var(--font-display)',
            fontStyle: 'italic',
            marginBottom: '16px',
            textAlign: 'center',
          }}>
            🔮 Число кармы
          </h3>
          {loading ? (
            <AnimatedLoadingState />
          ) : error ? (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <p style={{ color: '#ff6b6b', marginBottom: '16px' }}>{error}</p>
              <button onClick={loadKarma} style={buttonPrimaryStyle}>Повторить</button>
            </div>
          ) : karmaData ? (
            <KarmaNumber data={karmaData} />
          ) : (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <p style={{ color: '#888', marginBottom: '16px' }}>Нет данных для отображения</p>
              <button onClick={loadKarma} style={buttonPrimaryStyle}>
                Рассчитать
              </button>
            </div>
          )}
        </div>
      )}

      {/* ---------- Зрелость ---------- */}
      {activeTab === 'maturity' && (
        <div style={cardStyle}>
          <h3 style={{
            color: '#d4af37',
            fontSize: '18px',
            fontFamily: 'var(--font-display)',
            fontStyle: 'italic',
            marginBottom: '16px',
            textAlign: 'center',
          }}>
            ⭐ Число зрелости
          </h3>
          {loading ? (
            <AnimatedLoadingState />
          ) : error ? (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <p style={{ color: '#ff6b6b', marginBottom: '16px' }}>{error}</p>
              <button onClick={loadMaturity} style={buttonPrimaryStyle}>Повторить</button>
            </div>
          ) : maturityData ? (
            <MaturityNumber data={maturityData} />
          ) : (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <p style={{ color: '#888', marginBottom: '16px' }}>Нет данных для отображения</p>
              <button onClick={loadMaturity} style={buttonPrimaryStyle}>
                Рассчитать
              </button>
            </div>
          )}
        </div>
      )}

      {/* ---------- Баланс ---------- */}
      {activeTab === 'balance' && (
        <div style={cardStyle}>
          <h3 style={{
            color: '#d4af37',
            fontSize: '18px',
            fontFamily: 'var(--font-display)',
            fontStyle: 'italic',
            marginBottom: '16px',
            textAlign: 'center',
          }}>
            ⚖️ Число баланса
          </h3>
          {loading ? (
            <AnimatedLoadingState />
          ) : error ? (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <p style={{ color: '#ff6b6b', marginBottom: '16px' }}>{error}</p>
              <button onClick={loadBalance} style={buttonPrimaryStyle}>Повторить</button>
            </div>
          ) : balanceData ? (
            <BalanceNumber data={balanceData} />
          ) : (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <p style={{ color: '#888', marginBottom: '16px' }}>Нет данных для отображения</p>
              <button onClick={loadBalance} style={buttonPrimaryStyle}>
                Рассчитать
              </button>
            </div>
          )}
        </div>
      )}

      {/* ---------- Выживание ---------- */}
      {activeTab === 'survival' && (
        <div style={cardStyle}>
          <h3 style={{
            color: '#d4af37',
            fontSize: '18px',
            fontFamily: 'var(--font-display)',
            fontStyle: 'italic',
            marginBottom: '16px',
            textAlign: 'center',
          }}>
            🛡️ Число выживания
          </h3>
          {loading ? (
            <AnimatedLoadingState />
          ) : error ? (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <p style={{ color: '#ff6b6b', marginBottom: '16px' }}>{error}</p>
              <button onClick={loadSurvival} style={buttonPrimaryStyle}>Повторить</button>
            </div>
          ) : survivalData ? (
            <SurvivalNumber data={survivalData} />
          ) : (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <p style={{ color: '#888', marginBottom: '16px' }}>Нет данных для отображения</p>
              <button onClick={loadSurvival} style={buttonPrimaryStyle}>
                Рассчитать
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================
// Утилиты
// ============================================================

const MONTH_NAMES_RU = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
];

const numberCardStyle: React.CSSProperties = {
  background: 'rgba(10, 8, 20, 0.4)',
  border: '1px solid rgba(212, 175, 55, 0.15)',
  borderRadius: '16px',
  padding: '20px',
  textAlign: 'center',
};

function getScoreColor(score: number): string {
  if (score >= 80) return '#d4af37';
  if (score >= 60) return '#f4d03f';
  if (score >= 40) return '#FFAA44';
  return '#FF6666';
}

function formatDateRu(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' });
}

export default NumerologyPage;
